import * as THREE from 'three';
import { clamp } from '../utils.js';

const _euler = new THREE.Euler(0, 0, 0, 'YXZ');

/**
 * Kinematic ground-vehicle motion model.
 *
 * Parallels HelicopterFlightModel / FixedWingFlightModel — AI sets
 * desiredHeading + desiredSpeed each frame and the model chases them under
 * rate caps. Differences from the aerial models:
 *
 *   - No vertical input: Y is derived from terrain sampling at four contact
 *     points (front-left, front-right, back-left, back-right) defined by
 *     params.wheelbase and params.track.
 *   - Visual pitch/roll come from terrain slope (front-to-back and left-to-
 *     right height deltas), spring-damped so noisy bilinear samples and LOD
 *     seams don't twitch the body, plus a speed-scaled sum-of-sines bump sway
 *     standing in for surface roughness the heightmap is far too coarse to
 *     carry (its finest LOD samples are metres apart).
 *   - `disabled` flag (set externally when destroyed) bypasses AI and
 *     decelerates forward speed to zero — the body coasts to a halt while
 *     remaining visible.
 *
 * Forward convention: heading 0 → world -Z, matching the rest of the sim.
 */
export default class GroundVehicleMotionModel {
  constructor(params) {
    this.params = params;

    // Physics state
    this.position = new THREE.Vector3();
    this.velocity = new THREE.Vector3();
    this.heading = 0;
    this.forwardSpeed = 0;
    this.yawRate = 0;

    // Visual pitch/roll (spring-damped, terrain-driven)
    this.pitch = 0;
    this.roll = 0;
    this.pitchVel = 0;
    this.rollVel = 0;

    // AI inputs — set externally each frame
    this.desiredHeading = 0;
    this.desiredSpeed = 0;

    // Sum-of-sines bump sway state. Per-instance random phase so a column of
    // vehicles doesn't jostle in lock-step.
    this._bumpTime = 0;
    this._bumpPhase = [
      Math.random() * Math.PI * 2,
      Math.random() * Math.PI * 2,
      Math.random() * Math.PI * 2,
      Math.random() * Math.PI * 2,
      Math.random() * Math.PI * 2,
      Math.random() * Math.PI * 2,
    ];

    // Set externally by EnemyGroundVehicle once destroyed — coasts to a halt
    // and ignores AI input. There's no structural-failure / tumble equivalent
    // for vehicles since they sit on the ground; "death" is just a dead engine.
    this.disabled = false;

    // Cached last-good ground samples so we don't snap if a frame's terrain
    // queries return null (tile not streamed yet).
    this._lastGroundY = null;
    this._lastPitchTarget = 0;
    this._lastRollTarget = 0;

    this.quaternion = new THREE.Quaternion();
  }

  /**
   * @param {number} dt
   * @param {import('../world/TerrainCollider.js').default | null} terrainCollider
   */
  update(dt, terrainCollider = null) {
    const p = this.params;

    // 1. Heading — chase a desired yaw rate, capped. Same shape as the
    //    aerial flight models. While disabled, hold heading (no steering).
    if (!this.disabled) {
      let headingErr = this.desiredHeading - this.heading;
      while (headingErr >  Math.PI) headingErr -= Math.PI * 2;
      while (headingErr < -Math.PI) headingErr += Math.PI * 2;
      const desiredYawRate = clamp(headingErr * p.yawRateGain, -p.maxYawRate, p.maxYawRate);
      const yawAccelMax = p.yawAccel * dt;
      this.yawRate += clamp(desiredYawRate - this.yawRate, -yawAccelMax, yawAccelMax);
    } else {
      // Decay yaw rate so we settle straight.
      const decay = p.yawAccel * dt;
      if      (this.yawRate >  decay) this.yawRate -= decay;
      else if (this.yawRate < -decay) this.yawRate += decay;
      else                            this.yawRate  = 0;
    }
    this.heading += this.yawRate * dt;

    // 2. Forward speed — chase desiredSpeed under accel/brake caps. Disabled
    //    overrides the target to 0 and uses brake deceleration so the wreck
    //    rolls to a stop instead of snapping still.
    const target = this.disabled ? 0 : this.desiredSpeed;
    const speedErr = target - this.forwardSpeed;
    const accelCap = (speedErr >= 0 ? p.forwardAccel : p.brakeDecel) * dt;
    this.forwardSpeed += clamp(speedErr, -accelCap, accelCap);
    this.forwardSpeed = clamp(this.forwardSpeed, p.minSpeed, p.maxSpeed);

    // 3. Horizontal velocity and XZ integration.
    const fwdX = -Math.sin(this.heading);
    const fwdZ = -Math.cos(this.heading);
    this.velocity.x = fwdX * this.forwardSpeed;
    this.velocity.z = fwdZ * this.forwardSpeed;
    this.position.x += this.velocity.x * dt;
    this.position.z += this.velocity.z * dt;

    // 4. Terrain conformance — sample four contact points around the body
    //    (front-left, front-right, back-left, back-right) in world XZ. Y is
    //    the mean of the four samples + yOffset; pitch/roll come from the
    //    front-vs-back and left-vs-right height deltas divided by their
    //    respective baselines.
    const halfWB = p.wheelbase * 0.5;
    const halfTR = p.track * 0.5;
    // Body-frame basis in world XZ. Forward = -Z at heading 0 → (fwdX, fwdZ).
    // Right = perpendicular (rotated 90° CCW around +Y) = (cos h, -sin h).
    const rightX =  Math.cos(this.heading);
    const rightZ = -Math.sin(this.heading);

    const fx = this.position.x + fwdX * halfWB;
    const fz = this.position.z + fwdZ * halfWB;
    const bx = this.position.x - fwdX * halfWB;
    const bz = this.position.z - fwdZ * halfWB;

    const flX = fx - rightX * halfTR, flZ = fz - rightZ * halfTR;
    const frX = fx + rightX * halfTR, frZ = fz + rightZ * halfTR;
    const blX = bx - rightX * halfTR, blZ = bz - rightZ * halfTR;
    const brX = bx + rightX * halfTR, brZ = bz + rightZ * halfTR;

    let pitchTarget = this._lastPitchTarget;
    let rollTarget = this._lastRollTarget;
    let groundY = this._lastGroundY;

    if (terrainCollider) {
      const hFL = terrainCollider.getHeightAt(flX, flZ);
      const hFR = terrainCollider.getHeightAt(frX, frZ);
      const hBL = terrainCollider.getHeightAt(blX, blZ);
      const hBR = terrainCollider.getHeightAt(brX, brZ);
      if (hFL !== null && hFR !== null && hBL !== null && hBR !== null) {
        const frontAvg = (hFL + hFR) * 0.5;
        const backAvg  = (hBL + hBR) * 0.5;
        const leftAvg  = (hFL + hBL) * 0.5;
        const rightAvg = (hFR + hBR) * 0.5;

        // Pitch: nose-up when front is higher than back. In YXZ Euler with
        // forward = -Z, nose-up is a positive X-rotation.
        pitchTarget = Math.atan2(frontAvg - backAvg, p.wheelbase);
        // Roll: right side lower → bank right. In YXZ Euler with forward =
        // -Z and right = +X, right-down is a negative Z-rotation (positive Z
        // sends local +X to +Y, i.e. right-side-up / bank-left).
        rollTarget = Math.atan2(rightAvg - leftAvg, p.track);

        groundY = (hFL + hFR + hBL + hBR) * 0.25;
        this._lastGroundY = groundY;
        this._lastPitchTarget = pitchTarget;
        this._lastRollTarget = rollTarget;
      }
    }

    if (groundY !== null) {
      this.position.y = groundY + p.yOffset;
      this.velocity.y = 0;
    }

    // 5. Spring-damp pitch/roll toward the terrain-derived targets. Same
    //    formulation as the aerial flight models.
    const pitchAccel = -p.pitchStiffness * (this.pitch - pitchTarget) - p.pitchDamping * this.pitchVel;
    this.pitchVel += pitchAccel * dt;
    this.pitch    += this.pitchVel * dt;

    const rollAccel = -p.rollStiffness * (this.roll - rollTarget) - p.rollDamping * this.rollVel;
    this.rollVel += rollAccel * dt;
    this.roll    += this.rollVel * dt;

    // 6. Bump sway — sum-of-sines pseudo-noise for the surface roughness the
    //    heightmap can't resolve. Two harmonics per axis at irrational ratios
    //    so the pattern never visibly repeats. Both the amplitude *and* the
    //    phase rate scale with speed: the clock advances with ground covered
    //    rather than wall time, so slowing down stretches the bumps out instead
    //    of merely quietening them, and a halted (or destroyed, once coasted
    //    down) vehicle sits perfectly still. Purely visual — pitch/roll/heading
    //    bookkeeping is untouched, so the AI and followers see the true attitude.
    let bumpPitch = 0, bumpRoll = 0, bumpYaw = 0;
    if (p.bumpFrequency && p.maxSpeed > 0) {
      const speedFrac = clamp(Math.abs(this.forwardSpeed) / p.maxSpeed, 0, 1);
      this._bumpTime += dt * speedFrac;
      const t = this._bumpTime;
      const f = p.bumpFrequency * Math.PI * 2;
      const ph = this._bumpPhase;
      bumpPitch = p.bumpPitchAmp * speedFrac * (Math.sin(t * f       + ph[0]) + 0.5 * Math.sin(t * f * 1.7 + ph[1]));
      bumpRoll  = p.bumpRollAmp  * speedFrac * (Math.sin(t * f * 1.1 + ph[2]) + 0.5 * Math.sin(t * f * 1.9 + ph[3]));
      bumpYaw   = p.bumpYawAmp   * speedFrac * (Math.sin(t * f * 0.9 + ph[4]) + 0.5 * Math.sin(t * f * 1.5 + ph[5]));
    }

    // 7. Compose YXZ quaternion (yaw → pitch → roll) — apply bump sway last.
    _euler.set(this.pitch + bumpPitch, this.heading + bumpYaw, this.roll + bumpRoll);
    this.quaternion.setFromEuler(_euler);
  }
}
